/**
 * @file     ReadinessResultStep.tsx
 * @purpose  The free affordability estimate for the three-answer check,
 *           computed by the core lender-ceiling engine, with the opt-in broker
 *           referral sheet. Relta is not a mortgage broker: nothing here is a
 *           pre-approval or an amount a lender has agreed to. The referral fee
 *           is disclosed in the sheet, at the point of introduction.
 */
'use client';

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Link from 'next/link';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useVisitorProfile } from '@/hooks/useVisitorProfile';
import { queuePreferenceSyncMany } from '@/services/visitor-profile';
import { formatCAD } from '@/core/finance/mortgageBreakdown';
import { propertiesHref } from '@/core/config/places';
import { ResultHero, ToolAction } from '@/features/mortgage-calculator-flow/tool';
import { focusRingSx, hairline, hubColors, hubRadius } from '@/components/hub/hubKitStyles';
import { layoutTokens } from '@/design-system/tokens/layout';
import { FlowPresentationContext, useFlowPresentation } from '../components/FlowPresentation';
import { visuallyHidden } from '../components/flowKitTokens';
import {
  PRE_APPROVAL_BROKER_STEP,
  PRE_APPROVAL_FLOW_CONFIG,
  preApprovalAffordability,
} from '../configs/preApprovalFlow';
import { createLeadFlowSubmissionId } from '../submissionId';
import { CaptureStep } from './CaptureStep';
import type { FlowStep } from '../types';

const PROFILE_SOURCE = '/pre-approval-check';

const PROFILE_KEYS: Array<{ answerKey: string; profileKey: string }> = [
  { answerKey: 'incomeRange', profileKey: 'incomeRange' },
  { answerKey: 'monthlyDebtRange', profileKey: 'monthlyDebtRange' },
  { answerKey: 'downPaymentRange', profileKey: 'downPaymentRange' },
  { answerKey: 'location', profileKey: 'purchaseCity' },
];

const pct = (rate: number) => `${(rate * 100).toFixed(2)}%`;

type Props = {
  step: FlowStep;
  answers: Record<string, string>;
  onContinue: () => void;
  submissionId?: string;
  /** Set by the FlowRenderer when a capture already ran before this step. */
  verificationEmail?: string;
  /** Where the check was entered from; the broker lead carries it. */
  sourcePage?: string;
  /** Anchor for the phone result bar. */
  resultId?: string;
};

type Referral = { email: string; verified: boolean; delivered: boolean };

export const ReadinessResultStep = ({
  step,
  answers,
  submissionId,
  verificationEmail,
  sourcePage = PROFILE_SOURCE,
  resultId,
}: Props) => {
  const { t } = useTranslation();
  const { headingLevel } = useFlowPresentation();
  const { trackEvent } = useAnalytics();
  const { writeDataPoint } = useVisitorProfile();
  const titleId = useId();
  const fallbackSubmissionId = useRef<string | null>(null);
  const flowSubmissionId = submissionId ?? (fallbackSubmissionId.current ??= createLeadFlowSubmissionId());
  const [sheetOpen, setSheetOpen] = useState(false);
  const [referral, setReferral] = useState<Referral | null>(
    verificationEmail ? { email: verificationEmail, verified: false, delivered: false } : null,
  );
  const viewedRef = useRef(false);

  const estimate = useMemo(() => preApprovalAffordability(answers), [answers]);

  useEffect(() => {
    if (!estimate) return;
    for (const { answerKey, profileKey } of PROFILE_KEYS) {
      const value = answers[answerKey];
      if (value) writeDataPoint(profileKey, value, PROFILE_SOURCE);
    }
    queuePreferenceSyncMany(
      Object.fromEntries(
        PROFILE_KEYS.filter(({ answerKey }) => answers[answerKey]).map(({ answerKey, profileKey }) => [
          profileKey,
          answers[answerKey],
        ]),
      ),
    );
    if (viewedRef.current) return;
    viewedRef.current = true;
    trackEvent({
      eventName: 'pre_approval_readiness_result_viewed',
      eventType: 'funnel',
      category: 'lead_flow',
      label: 'Estimate',
      metadata: { readiness_band: 'Estimate', flow_id: PRE_APPROVAL_FLOW_CONFIG.flowId },
    });
    // The profile mirrors the answers; the event fires once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimate]);

  const awaitingEmail = Boolean(referral?.email) && !referral?.verified;

  useEffect(() => {
    if (!awaitingEmail) return;
    trackEvent({
      eventName: 'lead_email_confirmation_prompt_viewed',
      eventType: 'funnel',
      category: 'lead_flow',
      label: 'pre-approval',
      metadata: { flow_type: 'pre-approval', step_id: 'broker' },
    });
  }, [awaitingEmail, trackEvent]);

  // The broker referral itself is counted server-side, after FUB accepts the
  // contact (drainPendingLead). This poll only moves the sheet's status line.
  useEffect(() => {
    if (!referral?.email || referral.delivered) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const response = await fetch(
          `/api/leads/pending/status?submissionId=${encodeURIComponent(flowSubmissionId)}`,
          { cache: 'no-store' },
        );
        if (!response.ok) return;
        const json = (await response.json()) as { verified?: boolean; delivered?: boolean };
        if (cancelled) return;
        if (json.verified && !referral.verified) {
          trackEvent({
            eventName: 'lead_email_confirmed',
            eventType: 'funnel',
            category: 'lead_flow',
            label: 'pre-approval',
            metadata: { flow_type: 'pre-approval', step_id: 'broker' },
          });
        }
        if (json.verified || json.delivered) {
          setReferral((current) =>
            current
              ? { ...current, verified: current.verified || Boolean(json.verified), delivered: Boolean(json.delivered) }
              : current,
          );
        }
      } catch {
        // A transient poll failure is retried on the next tick.
      }
    };
    void poll();
    const timer = setInterval(poll, 4000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [flowSubmissionId, referral, trackEvent]);

  const heading = t(step.headlineKey, { defaultValue: step.headlineDefault });
  const homesHref = propertiesHref({ priceMax: estimate?.price });

  const statusLine = referral
    ? referral.delivered
      ? t('preApprovalFlow.broker.sent', {
          defaultValue: 'Ray is introducing you to one or more independent mortgage brokers. Each may contact you by email or phone.',
        })
      : referral.email
        ? t('preApprovalFlow.broker.confirm', {
            defaultValue:
              'Open the link sent to {{email}} so Ray can introduce you to mortgage brokers.',
            email: referral.email,
          })
        : t('preApprovalFlow.broker.sent', {
            defaultValue: 'Ray is introducing you to one or more independent mortgage brokers. Each may contact you by email or phone.',
          })
    : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box component={headingLevel} sx={visuallyHidden}>
        {heading}
      </Box>

      <ResultHero
        id={resultId}
        eyebrow={t('preApprovalFlow.result.eyebrow', { defaultValue: 'Estimate' })}
        label={t('preApprovalFlow.result.estimateLabel', { defaultValue: 'Home price your answers could carry' })}
        value={estimate ? t('preApprovalFlow.result.value', { defaultValue: 'About {{price}}', price: formatCAD(estimate.price) }) : null}
        emptyText={t('preApprovalFlow.result.empty', {
          defaultValue: 'Pick one answer in each group to see your estimate.',
        })}
        meaning={
          estimate
            ? t('preApprovalFlow.result.meaning', {
                defaultValue:
                  'With {{down}} down, a mortgage of about {{mortgage}}. At {{rate}}, about {{payment}} a month. Lenders test it at {{qualifying}}.',
                down: formatCAD(estimate.downPayment),
                mortgage: formatCAD(Math.round(estimate.ceiling.mortgage / 1000) * 1000),
                rate: pct(estimate.ceiling.contractRate),
                payment: formatCAD(estimate.monthlyPayment),
                qualifying: pct(estimate.ceiling.qualifyingRate),
              })
            : undefined
        }
        actions={
          estimate ? (
            <>
              <ToolAction href={homesHref}>
                {t('preApprovalFlow.result.seeHomes', { defaultValue: 'See homes at this price' })}
              </ToolAction>
              <ToolAction tone="secondary" ariaHaspopup="dialog" onClick={() => setSheetOpen(true)}>
                {referral
                  ? t('preApprovalFlow.result.brokerStatus', { defaultValue: 'Your broker request' })
                  : t('preApprovalFlow.result.sendFile', { defaultValue: 'Get introduced to mortgage brokers' })}
              </ToolAction>
            </>
          ) : undefined
        }
        disclaimer={t('preApprovalFlow.result.disclaimer', {
          defaultValue: 'An estimate, not a mortgage approval. Rates and lender rules vary.',
        })}
      >
        {statusLine ? (
          <Box role="status" sx={{ fontSize: 14, lineHeight: 1.55, color: hubColors.textPrimary, px: 2, py: 2, borderRadius: `${hubRadius.panel}px`, backgroundColor: hubColors.surfaceSunken }}>
            {statusLine}
          </Box>
        ) : null}
        {estimate ? (
          <Box component="p" sx={{ m: 0, fontSize: 13, lineHeight: 1.55, color: hubColors.textSecondary }}>
            {t('preApprovalFlow.result.assumptions', {
              defaultValue:
                'Based on a {{rate}} rate, tested at {{qualifying}}, over {{years}} years. Includes property tax, heat, half a typical strata fee, and mortgage insurance under 20% down.',
              rate: pct(estimate.ceiling.contractRate),
              qualifying: pct(estimate.ceiling.qualifyingRate),
              years: Math.round(estimate.ceiling.amortizationMonths / 12),
            })}
          </Box>
        ) : null}
      </ResultHero>

      <Dialog
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        aria-labelledby={titleId}
        fullWidth
        maxWidth="sm"
        sx={{
          '& .MuiDialog-container': { alignItems: { xs: 'flex-end', sm: 'center' } },
          '& .MuiDialog-paper': {
            m: { xs: 0, sm: 4 },
            width: '100%',
            maxWidth: { xs: '100%', sm: 560 },
            maxHeight: { xs: '92dvh', sm: 'calc(100% - 64px)' },
            borderRadius: { xs: `${layoutTokens.radius.xl}px ${layoutTokens.radius.xl}px 0 0`, sm: `${layoutTokens.radius.xl}px` },
            backgroundColor: hubColors.surfaceCard,
            backgroundImage: 'none',
            border: hairline,
            p: { xs: 4, sm: 6 },
            pb: { xs: 'max(20px, env(safe-area-inset-bottom))', sm: 4 },
          },
        }}
      >
        <Box id={titleId} sx={visuallyHidden}>
          {t(PRE_APPROVAL_BROKER_STEP.headlineKey, { defaultValue: PRE_APPROVAL_BROKER_STEP.headlineDefault })}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -1 }}>
          <Box
            component="button"
            type="button"
            onClick={() => setSheetOpen(false)}
            aria-label={t('common.close', { defaultValue: 'Close' })}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              mr: -1.5,
              mt: -2,
              border: 0,
              borderRadius: `${layoutTokens.radius.pill}px`,
              background: 'none',
              color: hubColors.textSecondary,
              cursor: 'pointer',
              '&:hover': { backgroundColor: hubColors.surfaceSunken },
              ...focusRingSx,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </Box>
        </Box>
        {referral ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box component="h2" sx={{ m: 0, fontSize: 22, fontWeight: 600, lineHeight: 1.25 }}>
              {referral.delivered || !referral.email
                ? t('preApprovalFlow.broker.sentTitle', { defaultValue: 'Sent' })
                : t('preApprovalFlow.broker.confirmTitle', { defaultValue: 'One step left' })}
            </Box>
            <Box component="p" role="status" sx={{ m: 0, fontSize: 15.5, lineHeight: 1.6, color: hubColors.textSecondary }}>
              {statusLine}
            </Box>
            <Box component="p" sx={{ m: 0, fontSize: 13.5, lineHeight: 1.55, color: hubColors.textSecondary }}>
              {t('preApprovalFlow.broker.after', {
                defaultValue: 'You are under no obligation to use any broker. How the referral fee works is on the',
              })}{' '}
              <Link href="/disclosure" style={{ color: 'inherit', fontWeight: 500 }}>
                {t('preApprovalFlow.broker.disclosureLink', { defaultValue: 'Referral disclosure' })}
              </Link>
              {' '}
              {t('preApprovalFlow.broker.afterTail', { defaultValue: 'page.' })}
            </Box>
            <ToolAction onClick={() => setSheetOpen(false)}>
              {t('preApprovalFlow.broker.done', { defaultValue: 'Done' })}
            </ToolAction>
          </Box>
        ) : (
          <FlowPresentationContext.Provider value={{ inShell: true, headingLevel: 'h2', density: 'regular' }}>
            <CaptureStep
              step={PRE_APPROVAL_BROKER_STEP}
              flowConfig={PRE_APPROVAL_FLOW_CONFIG}
              answers={answers}
              sourcePage={sourcePage}
              submissionId={flowSubmissionId}
              onSubmitSuccess={(email) => setReferral({ email: email ?? '', verified: false, delivered: !email })}
            />
          </FlowPresentationContext.Provider>
        )}
      </Dialog>
    </Box>
  );
};
